import React from 'react';
import { useLocation } from 'react-router-dom';
import type { Params } from './types';

const paramPattern = /(:[a-zA-Z]+)/g;

function indexOfPathEnd(route: string) {
  const queryStart = route.indexOf('?');
  return queryStart === -1 ? route.length : queryStart;
}

// Replace path paramters such as `:siteId`
function replacePathParams(route: string, data: Params = {}): string {
  return route.replace(paramPattern, (urlParam) => {
    const index = route.indexOf(urlParam);
    const paramKey = urlParam.slice(1);
    const datum = data[paramKey];

    if (datum) {
      return String(datum);
    }

    const currentParamEndIndex = index + urlParam.length;

    if (!datum && currentParamEndIndex === indexOfPathEnd(route)) {
      return '';
    }

    throw new Error(`No parameter provided for: '${paramKey}' in URL path '${route}'`);
  });
}

function replaceQueryParams(route: string, data: Params = {}): string {
  const queryParamStart = route.indexOf('?');

  // Replace query parameters in the given route.
  if (queryParamStart !== -1) {
    const searchParams = new URLSearchParams(route.substr(queryParamStart + 1));

    const toDelete: Array<string> = [];
    searchParams.forEach((_, key) => {
      const datum = data[key];
      if (datum) {
        searchParams.set(key, String(datum));
      } else {
        /**
         * We push keys onto an array rather than calling `searchParams.delete()` because calling delete
         * gets the indexes out of sync and the loop skips values in the array.
         */
        toDelete.push(key);
      }
    });

    toDelete.forEach((key) => searchParams.delete(key));

    const searchParamString = searchParams.toString();

    if (searchParamString.length > 0) {
      return `${route.substring(0, queryParamStart)}?${searchParams.toString()}`;
    }

    return route.substring(0, queryParamStart);
  }

  return route;
}

export function useQuery(): URLSearchParams {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

/**
 * This function **replaces** query & path params in a route template string.
 * - If any **path** param is missing in the `data` object, this function will throw an error
 * - **EXCEPT** if the missing param is the last path param in the `route`, it will be ignored and no errors are thrown.
 * - Query params are not added to the route but **replaced**. Any extra keys in the data object will be ignored.
 * - If a query param exists in `route` but no value was provided in `data`, it is not included in the output.
 *
 * @param route The route template string with format /route/:pathParam/:otherPathParam?queryParam1&queryParam2
 * @param data Values for the respective path & query params.
 * @returns The route string populated with the provided values.
 *
 * @example <caption>Example usage without params</caption>
 * // returns '/login'
 * getRoute('/login');
 *
 * @example <caption>Example usages with route params</caption>
 * // Returns '/users/23'
 * getRoute('/users/:userId', { userId: 23 });
 *
 * // Returns '/users/' although userId's value was not provided.
 * // Works only when the omitted param is the last path param in the route string.
 * getRoute('/users/:userId');
 *
 * // Throws an error for the missing path param `userId`
 * getRoute('/users/:userId/books');
 *
 * // Throws an error for the missing path param `userId`
 * getRoute('/users/:userId/books/:bookId', { bookId: 2 });
 *
 * @example <caption>Example usages with query params</caption>
 * // returns '/users/23'
 * // `firstVisit` is ignored as it was not provided in the `route`
 * getRoute('/users/:userId/', { userId: 23, firstVisit: true });
 *
 * // returns '/users?firstVisit=true'
 * // `userId` is ignored as its value is not provided in `data`.
 * // There is no error as `userId` is the last path param in the route.
 * getRoute('/users/:userId?firstVisit', { firstVisit: true });
 *
 * // returns '/users'
 * // `userId` and `firstVisit` are ignored as no value was provided for either of them.
 * // There is no error as `userId` is the last path param in the route.
 * getRoute('/users/:userId?firstVisit');
 */
export function getRoute(route: string, data: Params = {}): string {
  const parameterizedPath = replacePathParams(route, data);

  return replaceQueryParams(parameterizedPath, data);
}
